/* PBW: "You may also like" strip in the side cart.
   1. Loading: page loads only print an empty slot. When the side cart is open, the list is fetched from
      sections/pbw-cart-upsell.liquid and put in the slot, so working out suggestions never slows a page.
      Each cart (items and quantities) is fetched once and then reused.
   2. One-click add buttons. Uses the same cart events as the theme's own add to cart, so the drawer
      refreshes by itself. Safety net: if the drawer still shows the product we just added a moment later
      (for example when another line in the cart is sitting at its stock limit), the drawer is refreshed here. */
// The theme helpers are loaded only when a button is pressed, so the list still shows
// even if one of them is slow or blocked.

const release = (button) => {
  if (!button) return;
  button.disabled = false;
  button.classList.remove('is-loading');
};

const pbwUpsellCache = new Map();

const pbwUpsellLoad = (key) => {
  if (!pbwUpsellCache.has(key)) {
    const request = fetch(`${window.location.pathname}?section_id=pbw-cart-upsell`, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const wrapper = doc.querySelector('.shopify-section');
        return wrapper ? wrapper.innerHTML : text;
      })
      .catch((error) => {
        pbwUpsellCache.delete(key);
        console.warn('[pbw-upsell] could not load suggestions', error);
        return '';
      });
    pbwUpsellCache.set(key, request);
  }
  return pbwUpsellCache.get(key);
};

const pbwUpsellFill = async () => {
  const dialog = document.querySelector('#cart-drawer dialog');
  if (!dialog || !dialog.open) return;
  const slot = dialog.querySelector('[data-pbw-upsell-slot]');
  if (!slot) return;
  const key = slot.dataset.key || '';
  if (slot.dataset.filled === key || slot.dataset.loading === key) return;
  slot.dataset.loading = key;
  const html = await pbwUpsellLoad(key);
  const current = dialog.querySelector('[data-pbw-upsell-slot]');
  if (!current || (current.dataset.key || '') !== key) return;
  current.innerHTML = html;
  current.dataset.filled = key;
  delete current.dataset.loading;
};

const pbwUpsellWatch = () => {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      pbwUpsellFill();
    });
  }).observe(drawer, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'data-key'] });
  pbwUpsellFill();
};

if (!window.__pbwUpsellReady) {
  window.__pbwUpsellReady = true;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', pbwUpsellWatch);
  } else {
    pbwUpsellWatch();
  }

  document.addEventListener('click', async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-pbw-upsell-add]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    button.disabled = true;
    button.classList.add('is-loading');

    let CartLinesUpdateEvent, fetchConfig, morphSection;
    try {
      ({ CartLinesUpdateEvent } = await import('@shopify/events'));
      ({ fetchConfig } = await import('@theme/utilities'));
      ({ morphSection } = await import('@theme/section-renderer'));
    } catch (error) {
      console.error(error);
      release(button);
      return;
    }

    const variantId = button.dataset.variantId;
    const productId = button.dataset.productId;
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });
    const sectionId = sectionIds[0] || 'cart-drawer-section';
    formData.append('sections', sectionIds.length ? sectionIds.join(',') : sectionId);

    const deferred = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const json = await response.json();
      if (json.status) throw new Error(json.message || 'Could not add to cart');

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: AbortSignal.timeout(8000),
      });
      const ajaxCart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'pbw-upsell',
          sourceId: 'pbw-upsell',
          itemCount: 1,
          productId,
          sections: json.sections,
          didError: false,
        },
      });

      // Safety net: the added product should have left the strip by now.
      setTimeout(async () => {
        const stale = document.querySelector(`[data-pbw-upsell-add][data-variant-id="${variantId}"]`);
        if (!stale) return;
        try {
          let markup = json.sections && json.sections[sectionId];
          if (!markup) {
            const refreshed = await fetch(`${window.location.pathname}?section_id=${sectionId}`, { credentials: 'same-origin' });
            markup = await refreshed.text();
          }
          await morphSection(sectionId, markup, { mode: 'full' });
        } catch (error) {
          console.warn('[pbw-upsell] refresh failed', error);
        } finally {
          document
            .querySelectorAll('[data-pbw-upsell-add].is-loading, [data-pbw-upsell-add]:disabled')
            .forEach((el) => release(el));
        }
      }, 1500);
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      release(button);
    }
  });
}
